import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DialogResult, PopupDialog } from '@app/Common';
import { ProvisionedApplicationsTypes } from '@app/Enums';
import { Tenant } from '@app/Models/tenant';
import { TenantEnvironmentDetails } from '@app/Models/tenant-environment-details.model';
import { TenantService } from '@app/Services';
import { Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/internal/operators/filter';
import { map } from 'rxjs/internal/operators/map';
import { OptionCategory } from '../../Models';
import { CopyConfigService } from '../../Services';
import { finalize, takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { JobStatusPlaceholderComponent } from '../../../../../Common/job-status-placeholder/job-status-placeholder.component';
import { MatButton } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatCheckbox } from '@angular/material/checkbox';
import { NoDataPlaceholderComponent } from '../../../../../Common/no-data-placeholder/no-data-placeholder.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
    selector: 'app-copy-config-export-import',
    templateUrl: './copy-config-export-import.component.html',
    styleUrls: [
        './copy-config-export-import.component.scss'
    ],
    imports: [JobStatusPlaceholderComponent, MatButton, FormsModule, MatSelect, MatOption, MatCard, MatCardContent, MatIcon, MatTooltip, MatCheckbox, NoDataPlaceholderComponent, AsyncPipe, MatProgressSpinner]
})
export class CopyConfigExportImportComponent implements OnInit, OnDestroy {
  private isDestroyed$: Subject<void> = new Subject();
  copyConfigInProgress$: Observable<boolean>;
  copyConfigFinalised$: Observable<boolean>;
  copyFromTenantValue: string;
  copyToTenantValue: string;
  copyFromTenant: Tenant;
  copyToTenant: Tenant;
  copyFromTenant$: Observable<Tenant>;
  copyToTenant$: Observable<Tenant>;
  copyToTenantOptions$: Observable<Tenant[]>;
  copyFromEnvironment$: Observable<string>;
  copyToEnvironment$: Observable<string>;
  showProgressBar = false;
  showInProgressMessage = false;
  isCheckingForChanges = false;

  constructor(
    private tenantService: TenantService,
    private copyConfigService: CopyConfigService,
    private dialog: MatDialog,
    private router: Router,
  ) {
    this.copyConfigInProgress$ = this.copyConfigService.copyConfigInProgress$;
    this.copyConfigFinalised$ = this.copyConfigService.copyConfigFinalised$;
    this.copyFromTenant$ = this.copyConfigService.sourceTenant$;
    this.copyToTenant$ = this.copyConfigService.targetTenant$;
  }

  ngOnInit(): void {
    this.copyConfigService.loadDefaultOptionsCategories();

    this.setDefaultTenantFromValue();
    this.changeCopyFrom();
  }

  ngOnDestroy(): void {
    this.isDestroyed$.next();
  }

  copyAnotherTenant(): void {
    location.reload();
  }

  isAllOptionsChecked(category: OptionCategory): boolean {
    if (category.options.length === 0) {
      return false;
    }
    return category.options.every(option => option.Checked);
  }

  isSomeOptionsChecked(category: OptionCategory): boolean {
    if (category.options.length === 0 || this.isAllOptionsChecked(category)) {
      return false;
    }
    return category.options.some(option => option.Checked);
  }

  setAllOptions(category: OptionCategory, checked: boolean): void {
    category.options.forEach(option => option.Checked = checked);
  }

  categoryHasOptions(category: OptionCategory): boolean {
    return category.options.length > 0;
  }

  isSelectAllButtonAvailable(category: OptionCategory): boolean {
    return this.categoryHasOptions(category) && category.allowToggleAllOptions;
  }

  getCopyFromTenantOptions(): Tenant[] {
    return this.tenantService.tenants.filter((tenant) =>
      tenant.ProvisionedApplications.includes(ProvisionedApplicationsTypes.Airsweb)
    );
  }

  getOptionsCategories(): OptionCategory[] {
    return this.copyConfigService.optionsCategories;
  }

  changeCopyFrom(): void {
    if (!this.copyFromTenantValue) {
      return;
    }

    this.copyFromTenant = this.getCopyFromTenantOptions().find(
      (tenant) => tenant.Value === this.copyFromTenantValue
    );
    this.copyToTenantOptions$ = this.tenantService.getTenantsEnviromentByTenantId(this.copyFromTenant.TenantId).pipe(
      map(tenants => tenants.filter(tenant => tenant.Value !== this.copyFromTenantValue))
    );
    this.copyFromEnvironment$ = this.getEnvironmentForTenant(
      this.copyFromTenantValue
    );

    if (this.copyToTenantValue && this.copyFromTenantValue === this.copyToTenantValue) {
      this.copyToTenantValue = null;
    }

    this.copyConfigService.loadTenantModuleConfigOptions(this.copyFromTenantValue, this.copyToTenantValue);
  }

  changeCopyTo(): void {
    if (this.copyToTenantValue) {
      this.copyToTenant = this.getCopyFromTenantOptions()
        .find((tenant) => tenant.Value === this.copyToTenantValue);
      this.copyToEnvironment$ = this.getEnvironmentForTenant(this.copyToTenantValue);

      this.copyConfigService.getCopyConfigLogs(this.copyToTenant.TenantId)
        .pipe(takeUntil(this.isDestroyed$))
        .subscribe(logs => {
          if (logs?.find(log => !log.Finished && !log.HasErrors)) {
            this.showInProgressMessage = true;
          }
          else {
            this.showInProgressMessage = false;
            this.copyConfigService.loadTenantModuleConfigOptions(this.copyFromTenantValue, this.copyToTenantValue);
          }
        });
    } else {
      this.copyConfigService.loadTenantModuleConfigOptions(this.copyFromTenantValue, this.copyToTenantValue);
    }

        
  }

  goToCopyConfigLogs(): void {
    this.router.navigate([`tenant/${this.copyToTenant.TenantId}/airsweb/copyconfig/logs`]);
}

  copyConfigConfirm(): void {
    if (this.isCheckingForChanges) {
      return;
    }

    const moduleIds = this.copyConfigService.getModuleConfigCategory().options
      .filter((option) => option.Checked)
      .map((option) => option.FormFlowModuleId);

    this.isCheckingForChanges = true;
    this.copyConfigService.getModulesWithChanges(this.copyToTenant?.TenantId, moduleIds)
      .pipe(
        finalize(() => this.isCheckingForChanges = false),
        takeUntil(this.isDestroyed$)
      )
      .subscribe((modulesWithChanges) => this.openCopyConfigConfirmDialog(modulesWithChanges));
  }

  private openCopyConfigConfirmDialog(modulesWithChanges: string[]): void {
    const dialogRef =
      this.dialog.open<PopupDialog, any, DialogResult>(PopupDialog);
    dialogRef.componentInstance.DialogTitle = 'Copy Configuration';

    let description =
      `Are you sure you want to Copy Config from <b>${this.copyFromTenantValue}</b> to <b>${this.copyToTenantValue}</b>?<br><br>` +
      `This will overwrite the selected modules' draft configuration on the destination tenant, discarding any unpublished changes there.`;
    if (modulesWithChanges.length > 0) {
      description += `<br><br><b>The following destination modules have unpublished changes that will be lost:</b> ${modulesWithChanges.join(', ')}`;
    }
    dialogRef.componentInstance.DialogDescription = description;
    dialogRef.componentInstance.DialogConfirmButtonText = 'Confirm';
    dialogRef.componentInstance.DialogCancelButtonText = 'Cancel';

    dialogRef.afterClosed().subscribe((result) => {
      if (result !== undefined && result.Result) {
        this.copyConfigService.copyConfigInProgress$.next(true);
        this.showProgressBar = true;
        this.copyConfig();
      } else {
        this.copyConfigService.copyConfigInProgress$.next(false);
      }
    });
  }

  copyConfig(): void {
    const targetTenant = this.tenantService.tenants.find(
      (tenant) => tenant.Value === this.copyToTenantValue
    );
    if (!targetTenant) {
      this.showProgressBar = false;
    }

    this.copyConfigService.copyConfigExportImportRequest(this.copyFromTenantValue, this.copyToTenantValue)
      .subscribe((jobId) => {
        this.copyConfigService.sourceTenant$.next(this.copyFromTenant);
        this.copyConfigService.targetTenant$.next(this.copyToTenant);
        this.copyConfigService.copyConfigJobId$.next(jobId);
      });
  }

  isCopyConfigAvailable(): boolean {
    return (
      !!this.copyToTenantValue &&
      !!this.copyFromTenantValue &&
      this.copyToTenantValue !== this.copyFromTenantValue
    );
  }

  isCopyConfigButtonDisabled(): boolean {
    return !this.isCopyConfigAvailable() || this.showInProgressMessage || this.isCheckingForChanges;
  }

  private getEnvironmentForTenant(tenantName: string): Observable<string> {
    return this.tenantService
      .getEnvironmentDetailsByTenant(tenantName)
      .pipe(
        filter((environemntDetails: TenantEnvironmentDetails | null) => environemntDetails !== null),
        map((environemntDetails: TenantEnvironmentDetails) =>
          environemntDetails.EnvironmentName
        )
      );
  }

  private setDefaultTenantFromValue(): void {
    if (!this.tenantService.selectedTenant) {
      return;
    }
    this.copyFromTenant = this.tenantService.selectedTenant;
    this.copyFromTenantValue = this.copyFromTenant.Value;
  }
}
